import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import apiClient from '../config/axios';
import { useWorkspace } from '../contexts/WorkspaceContext';
import {
  Loader2,
  MessageSquare,
  ShieldCheck,
  ExternalLink,
  Copy,
  ArrowRight,
  AlertCircle,
  Eye,
  EyeOff
} from 'lucide-react';

const FALLBACK_APP_ID = process.env.REACT_APP_META_APP_ID || '';
const FALLBACK_CONFIG_ID = process.env.REACT_APP_META_EMBEDDED_SIGNUP_CONFIG_ID || '';
const FALLBACK_GRAPH_VERSION = process.env.REACT_APP_META_GRAPH_VERSION || 'v21.0';

const SDK_LOAD_TIMEOUT_MS = 25000;

/**
 * Meta Embedded Signup: FB.login yields `code`; `postMessage` yields WABA + phone_number_id.
 * App ID + config ID come from GET /embedded-signup/client-config (server env) so production
 * does not depend on REACT_APP_* at build time.
 * @see https://developers.facebook.com/documentation/business-messaging/whatsapp/embedded-signup/implementation
 */
const WhatsAppOnboardingWizard = () => {
  const { currentWorkspace } = useWorkspace();
  const queryClient = useQueryClient();
  const [step, setStep] = useState(1);
  const [metaAppId, setMetaAppId] = useState('');
  const [embeddedConfigId, setEmbeddedConfigId] = useState('');
  const [graphVersion, setGraphVersion] = useState(FALLBACK_GRAPH_VERSION);
  const [configLoading, setConfigLoading] = useState(true);
  const [configFetchError, setConfigFetchError] = useState('');
  const [fbReady, setFbReady] = useState(false);
  const [sdkError, setSdkError] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);
  const [metaPin, setMetaPin] = useState('');
  const [showPin, setShowPin] = useState(false);

  const pendingCodeRef = useRef(null);
  const pendingSessionRef = useRef(null);
  const completingRef = useRef(false);
  const sdkLoadTimeoutRef = useRef(null);
  // Keep a ref to metaPin so tryComplete always reads the latest value without
  // needing it in the useCallback dependency array (avoids stale closures).
  const metaPinRef = useRef(metaPin);
  useEffect(() => { metaPinRef.current = metaPin; }, [metaPin]);

  const configured = Boolean(String(metaAppId || '').trim() && String(embeddedConfigId || '').trim());

  const tryComplete = useCallback(async () => {
    const code = pendingCodeRef.current;
    const session = pendingSessionRef.current;
    if (!code || !session?.waba_id || !session?.phone_number_id) return;
    if (completingRef.current) return;
    if (!currentWorkspace?.id) return;

    // Mark done immediately so a second postMessage / FB.login callback doesn't re-enter
    completingRef.current = true;
    pendingCodeRef.current = null;   // consume the code — prevents duplicate submissions
    pendingSessionRef.current = null;

    setConnecting(true);
    setError('');
    const pin = metaPinRef.current;  // read latest value via ref — avoids stale closure
    try {
      const res = await apiClient.post(
        '/api/whatsapp/embedded-signup/complete',
        {
          code,
          waba_id: session.waba_id,
          phone_number_id: session.phone_number_id,
          pin: pin ? String(pin).trim() : undefined
        },
        { headers: { 'x-workspace-id': String(currentWorkspace.id) } }
      );
      setError('');
      setSuccess(res.data);
      setStep(4);
      queryClient.invalidateQueries(['whatsappSetupStatus']);
      toast.success('WhatsApp connected for this workspace.');
    } catch (e) {
      // Restore refs so user can retry without re-opening FB dialog
      pendingCodeRef.current = code;
      pendingSessionRef.current = session;
      const apiCode = e.response?.data?.code;
      const msg = apiCode === 'META_PIN_REQUIRED'
        ? (e.response?.data?.message || 'Meta requires your WhatsApp PIN to register this number.')
        : (
          e.response?.data?.error ||
          e.response?.data?.message ||
          e.message ||
          'Could not complete setup.'
        );
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
      toast.error('Setup failed');
    } finally {
      setConnecting(false);
      completingRef.current = false;
    }
  }, [currentWorkspace?.id, queryClient]); // metaPin read via ref — no dep needed

  useEffect(() => {
    const onMessage = (event) => {
      if (typeof event.origin !== 'string' || !event.origin.includes('facebook.com')) return;
      let payload;
      try {
        payload = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
      } catch {
        return;
      }
      if (!payload || payload.type !== 'WA_EMBEDDED_SIGNUP' || !payload.data) return;

      const ev = String(payload.event || '');
      const d = payload.data;
      const wabaId = d.waba_id || (Array.isArray(d.waba_ids) && d.waba_ids[0]) || null;
      const phoneId = d.phone_number_id || null;

      if (ev.includes('FINISH') && wabaId && phoneId) {
        pendingSessionRef.current = { waba_id: wabaId, phone_number_id: phoneId };
        // FB.login callback (code) and postMessage (session) can arrive in either order.
        // Give the FB.login callback up to 500 ms to store its code before trying.
        if (pendingCodeRef.current) {
          tryComplete();
        } else {
          setTimeout(() => tryComplete(), 500);
        }
      } else if (ev.includes('FINISH') && wabaId && !phoneId) {
        toast(
          'WhatsApp Business Account linked. Add a phone number in Meta Business Suite, or enter IDs manually under Settings.',
          { duration: 6000 }
        );
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [tryComplete]);

  // Load Meta app ID + Embedded Signup config from API (server .env), then fall back to REACT_APP_*.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setConfigFetchError('');
      try {
        const res = await apiClient.get('/api/whatsapp/embedded-signup/client-config');
        if (cancelled) return;
        const appId = (res.data?.appId || FALLBACK_APP_ID || '').trim();
        const cfgId = (res.data?.configId || FALLBACK_CONFIG_ID || '').trim();
        const gv = (res.data?.graphVersion || FALLBACK_GRAPH_VERSION || 'v21.0').trim() || 'v21.0';
        setMetaAppId(appId);
        setEmbeddedConfigId(cfgId);
        setGraphVersion(gv);
      } catch (e) {
        if (!cancelled) {
          setMetaAppId(FALLBACK_APP_ID);
          setEmbeddedConfigId(FALLBACK_CONFIG_ID);
          setGraphVersion(FALLBACK_GRAPH_VERSION);
          setConfigFetchError(
            e.response?.data?.error ||
              e.message ||
              'Could not load Meta config from server; using build-time env if present.'
          );
        }
      } finally {
        if (!cancelled) setConfigLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Load Facebook SDK and init when we have app ID.
  useEffect(() => {
    if (configLoading || !configured) {
      setFbReady(false);
      setSdkError('');
      if (sdkLoadTimeoutRef.current) {
        clearTimeout(sdkLoadTimeoutRef.current);
        sdkLoadTimeoutRef.current = null;
      }
      return;
    }

    setFbReady(false);
    setSdkError('');

    const clearTimer = () => {
      if (sdkLoadTimeoutRef.current) {
        clearTimeout(sdkLoadTimeoutRef.current);
        sdkLoadTimeoutRef.current = null;
      }
    };

    const initFb = () => {
      if (!window.FB) return;
      window.FB.init({
        appId: metaAppId,
        cookie: true,
        xfbml: true,
        version: graphVersion
      });
      clearTimer();
      setFbReady(true);
    };

    sdkLoadTimeoutRef.current = setTimeout(() => {
      if (!window.FB) {
        setSdkError(
          'Facebook SDK did not load in time. Check ad blockers, network, or try another browser. You can still use manual setup below.'
        );
        setFbReady(false);
      }
    }, SDK_LOAD_TIMEOUT_MS);

    if (window.FB) {
      initFb();
      return clearTimer;
    }

    window.fbAsyncInit = initFb;
    const id = 'facebook-jssdk';
    const existing = document.getElementById(id);
    if (existing) {
      const t = setInterval(() => {
        if (window.FB) {
          clearInterval(t);
          initFb();
        }
      }, 100);
      return () => {
        clearInterval(t);
        clearTimer();
      };
    }

    const js = document.createElement('script');
    js.id = id;
    js.async = true;
    js.defer = true;
    js.crossOrigin = 'anonymous';
    js.src = 'https://connect.facebook.net/en_US/sdk.js';
    document.body.appendChild(js);

    return () => {
      clearTimer();
    };
  }, [configLoading, configured, metaAppId, graphVersion, embeddedConfigId]);

  const startFacebook = () => {
    setError('');
    if (!configured) {
      setError('Missing Meta app ID or Embedded Signup config ID. Set them on the server or in client env and restart.');
      return;
    }
    if (!window.FB || !fbReady) {
      setError(sdkError || 'Facebook SDK is still loading. Try again in a moment.');
      return;
    }

    pendingCodeRef.current = null;
    pendingSessionRef.current = null;

    window.FB.login(
      (response) => {
        const code = response?.authResponse?.code;
        if (code) {
          pendingCodeRef.current = code;
          tryComplete();
        } else if (response?.status === 'not_authorized' || response?.status === 'unknown') {
          setError('Facebook login was cancelled or failed.');
        }
      },
      {
        config_id: embeddedConfigId,
        response_type: 'code',
        override_default_response_type: true,
        extras: {
          setup: {},
          featureType: 'whatsapp_business_app_onboarding',
          sessionInfoVersion: '3'
        }
      }
    );
  };

  const copyToken = () => {
    const t = success?.verify_token;
    if (!t) return;
    navigator.clipboard.writeText(t).then(() => toast.success('Verify token copied'));
  };

  const waitingForSdk = configured && !fbReady && !sdkError && !configLoading;
  /** Block click while config/sdk loads; if SDK failed (sdkError), allow Retry (reload). */
  const facebookButtonDisabled =
    configLoading ||
    connecting ||
    ((!configured || (configured && !fbReady)) && !sdkError);

  const buttonLabel = () => {
    if (configLoading) return 'Loading configuration…';
    if (!configured) return 'Configure Meta app ID & Embedded Signup';
    if (sdkError) return 'Retry Facebook load';
    if (connecting) return 'Connecting…';
    if (waitingForSdk) return 'Loading Facebook…';
    return 'Continue with Facebook';
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg">
          <MessageSquare className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-heading font-bold text-gray-900">Connect WhatsApp (Meta)</h1>
          <p className="text-sm text-gray-600">Embedded Signup — sign in with Facebook and select your WhatsApp assets.</p>
        </div>
      </div>

      {configFetchError && (
        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900 text-sm">
          <strong className="font-semibold">Config request: </strong>
          {configFetchError}
        </div>
      )}

      {!configured && !configLoading && (
        <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex gap-3 text-amber-900 text-sm">
          <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold">Meta app not fully configured</p>
            <p className="mt-1 text-amber-800">
              On the <strong>server</strong>, set <code className="bg-amber-100 px-1 rounded">META_APP_ID</code>,{' '}
              <code className="bg-amber-100 px-1 rounded">META_EMBEDDED_SIGNUP_CONFIG_ID</code> (Embedded Signup config
              ID from Meta), and <code className="bg-amber-100 px-1 rounded">META_APP_SECRET</code>. Restart the API server
              after changing <code className="bg-amber-100 px-1 rounded">.env</code>.
            </p>
            <p className="mt-2 text-amber-800">
              For local dev only, you can also set <code className="bg-amber-100 px-1 rounded">REACT_APP_META_APP_ID</code>{' '}
              and <code className="bg-amber-100 px-1 rounded">REACT_APP_META_EMBEDDED_SIGNUP_CONFIG_ID</code> in{' '}
              <code className="bg-amber-100 px-1 rounded">client/.env</code> and run <code className="bg-amber-100 px-1 rounded">npm start</code> again (Create React App reads env at start time).
            </p>
          </div>
        </div>
      )}

      <div className="mt-8 space-y-6">
        {step < 4 && (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <span className={step >= 1 ? 'font-semibold text-emerald-700' : ''}>1. Overview</span>
            <span>→</span>
            <span className={step >= 2 ? 'font-semibold text-emerald-700' : ''}>2. Checklist</span>
            <span>→</span>
            <span className={step >= 3 ? 'font-semibold text-emerald-700' : ''}>3. Facebook</span>
          </div>
        )}

        {step === 1 && (
          <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">What this does</h2>
            <p className="text-gray-600 text-sm leading-relaxed">
              You&apos;ll sign in with Facebook, grant access to your Business Portfolio, and pick the WhatsApp Business
              Account and phone number to use with Cloud API. We store the token and IDs for this workspace only.
            </p>
            <button
              type="button"
              onClick={() => setStep(2)}
              className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-emerald-600 text-white font-semibold hover:bg-emerald-700 transition-colors"
            >
              Continue <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Before you start</h2>
            <ul className="list-disc pl-5 space-y-2 text-sm text-gray-700">
              <li>You need a Meta Business Account and admin access to the WhatsApp Business Account you want to connect.</li>
              <li>Embedded Signup must be enabled on your Meta app (Facebook Login for Business + configuration ID).</li>
              <li>Use this flow as workspace owner or admin — members cannot connect API credentials.</li>
            </ul>
            <div className="mt-6 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => setStep(3)}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-emerald-600 text-white font-semibold hover:bg-emerald-700 transition-colors"
              >
                Ready — open Facebook <ArrowRight className="w-4 h-4" />
              </button>
              <Link
                to="/settings?tab=whatsapp"
                className="inline-flex items-center px-4 py-2.5 rounded-xl border border-gray-300 text-gray-700 font-medium hover:bg-gray-50"
              >
                Enter credentials manually
              </Link>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Sign in with Facebook</h2>
            <p className="text-sm text-gray-600 mb-4">
              Complete the Meta dialog: choose your business, WABA, and phone number. When both the OAuth code and asset
              data are ready, we connect automatically.
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">WhatsApp PIN (optional)</label>
              <div className="relative">
                <input
                  type={showPin ? 'text' : 'password'}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={metaPin}
                  onChange={(e) => setMetaPin(e.target.value.replace(/\D/g, '').slice(0, 8))}
                  placeholder="Enter PIN only if Meta asks for it"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
                <button
                  type="button"
                  onClick={() => setShowPin((v) => !v)}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 hover:text-gray-600"
                  tabIndex={-1}
                  aria-label={showPin ? 'Hide PIN' : 'Show PIN'}
                >
                  {showPin ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Required only when your WhatsApp number has 2-step verification enabled in Meta.
              </p>
            </div>
            {sdkError && (
              <div className="mb-4 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800">{sdkError}</div>
            )}
            {error && (
              <div className="mb-4 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800">{error}</div>
            )}
            <button
              type="button"
              disabled={facebookButtonDisabled}
              onClick={sdkError ? () => window.location.reload() : startFacebook}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#1877F2] text-white font-semibold hover:bg-[#166fe5] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {(configLoading || waitingForSdk || connecting) && !sdkError ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {buttonLabel()}
                </>
              ) : (
                buttonLabel()
              )}
            </button>
            <p className="mt-4 text-xs text-gray-500">
              Manual fallback:{' '}
              <Link to="/settings?tab=whatsapp" className="text-emerald-700 font-medium hover:underline">
                Settings → WhatsApp
              </Link>
            </p>
          </div>
        )}

        {step === 4 && success && (
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50/80 p-6 shadow-sm">
            <div className="flex items-start gap-3">
              <ShieldCheck className="w-8 h-8 text-emerald-700 shrink-0" />
              <div className="flex-1 min-w-0">
                <h2 className="text-lg font-semibold text-gray-900">Connected</h2>
                <p className="text-sm text-gray-700 mt-1">{success.message || 'WhatsApp is linked to this workspace.'}</p>
                {success.register_warning && (
                  <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800">
                    <p className="font-medium">Note: {success.register_warning.message}</p>
                  </div>
                )}
                {success.verify_token && (
                  <div className="mt-4 rounded-lg bg-white border border-emerald-200 p-3">
                    <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Webhook verify token</p>
                    <div className="mt-1 flex items-center gap-2 flex-wrap">
                      <code className="text-sm break-all text-gray-900 flex-1 min-w-0">{success.verify_token}</code>
                      <button
                        type="button"
                        onClick={copyToken}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-lg border border-gray-300 text-sm hover:bg-gray-50"
                      >
                        <Copy className="w-4 h-4" /> Copy
                      </button>
                    </div>
                    <p className="text-xs text-gray-600 mt-2">{success.webhook_hint}</p>
                  </div>
                )}
                <div className="mt-6 flex flex-wrap gap-3">
                  <Link
                    to="/settings?tab=whatsapp"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-emerald-600 text-white font-semibold hover:bg-emerald-700"
                  >
                    Open WhatsApp settings <ExternalLink className="w-4 h-4" />
                  </Link>
                  <Link to="/chats" className="inline-flex items-center px-4 py-2.5 rounded-xl border border-gray-300 text-gray-800 font-medium hover:bg-white">
                    Go to Chats
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WhatsAppOnboardingWizard;
